import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';
import { logger } from '../../lib/logger';
import type { AppDispatch, RootState } from '../index';
import { addNotification, updateUnreadCount } from '../slices/notificationsSlice';

interface NotificationResponse {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

// Fetch notifications from API (replaces direct database access)
export const fetchNotificationsFromDB = createAsyncThunk<
  NotificationResponse[],
  void,
  { state: RootState }
>('notifications/fetchFromDB', async (_, { getState }) => {
  const state = getState();
  const userId = state.clerkAuth.ezhoaUser?.id;

  if (!userId) {
    logger.warn('User not authenticated for notifications');
    return [];
  }

  // Call API endpoint instead of direct database access
  const notifications = await apiService.getNotifications();
  return notifications as unknown as NotificationResponse[];
});

// Create notification via API (replaces direct database access)
export const createNotificationInDB = createAsyncThunk<
  NotificationResponse,
  { title: string; message: string; type: string },
  { state: RootState }
>('notifications/createInDB', async (notificationData, { getState, dispatch }) => {
  const state = getState();
  const userId = state.clerkAuth.ezhoaUser?.id;

  if (!userId) {
    logger.error('User not authenticated - cannot create notification');
    throw new Error('User not authenticated');
  }

  // TODO: Implement POST /api/notifications endpoint in Functions
  // For now, create notification locally in Redux store
  const tempNotification: NotificationResponse = {
    id: `temp-${Date.now()}`,
    userId,
    type: notificationData.type,
    title: notificationData.title,
    message: notificationData.message,
    read: false,
    createdAt: new Date().toISOString(),
  };

  // Add to Redux store
  dispatch(
    addNotification({
      id: tempNotification.id,
      type: notificationData.type as
        | 'request'
        | 'payment'
        | 'document'
        | 'message'
        | 'announcement',
      title: notificationData.title,
      message: notificationData.message,
      timestamp: tempNotification.createdAt,
      read: false,
      userId,
      priority: 'medium',
    })
  );

  return tempNotification;
});

// Mark notification as read via API (replaces direct database access)
export const markNotificationAsReadInDB = createAsyncThunk(
  'notifications/markAsReadInDB',
  async (notificationId: string) => {
    // TODO: Implement PATCH /api/notifications/:id endpoint in Functions
    // For now, just return the ID to update Redux store
    return notificationId;
  }
);

// Helper function to create notifications for common events
export const notificationHelpers = {
  requestCreated: (dispatch: AppDispatch, requestTitle: string) => {
    dispatch(
      createNotificationInDB({
        title: 'Request Submitted',
        message: `Your request "${requestTitle}" has been submitted successfully.`,
        type: 'request',
      })
    );
  },

  requestStatusUpdated: (dispatch: AppDispatch, requestTitle: string, newStatus: string) => {
    dispatch(
      createNotificationInDB({
        title: 'Request Status Updated',
        message: `Your request "${requestTitle}" is now ${newStatus}.`,
        type: 'request',
      })
    );
  },

  documentUploaded: (dispatch: AppDispatch, documentName: string) => {
    dispatch(
      createNotificationInDB({
        title: 'Document Uploaded',
        message: `Document "${documentName}" has been uploaded successfully.`,
        type: 'document',
      })
    );
  },

  paymentReceived: (dispatch: AppDispatch, amount: number) => {
    dispatch(
      createNotificationInDB({
        title: 'Payment Received',
        message: `Payment of $${amount} has been received.`,
        type: 'payment',
      })
    );
  },

  messageReceived: (dispatch: AppDispatch, from: string) => {
    dispatch(
      createNotificationInDB({
        title: 'New Message',
        message: `You have a new message from ${from}.`,
        type: 'message',
      })
    );
  },
};
